/**
 * DraftCommentsPanel — the comments on the latest private draft, for the owner and the delegate while they edit it
 * (wireframe richEditor; Ownership design, part 3). "Comments on text that has since changed" sit right above dev's MDX
 * editor; each commented sentence's stacked comments and replies sit directly ABOVE that sentence in the editor, as
 * the wireframe's cards over their line. Whoever holds the JD resolves: a thread as a whole, a separate comment alone.
 * Lexical owns the editor's DOM, so nothing is inserted into it: the cards are rendered (a portal) into the editor's
 * scroll area, over a top margin set on the sentence's block (Lexical keeps an element's style and does not observe
 * attribute changes), and the commented sentences are highlighted with the CSS Custom Highlight API. A sentence whose
 * block can't be found (the editor not filled in yet, or hidden in Source / Diff view) is listed above the editor.
 *   comments     — the open comments ({ feedback_id, anchor_text, text, reply_to, author_name, created_at })
 *   markdown     — the text being edited (a sentence no longer in it moves to the list at the top)
 *   canResolve   — the viewer holds the JD
 *   busy, onResolve(feedbackId)
 *   editorRootRef — the editor's scroll area (position: relative), for the cards and the highlight. The panel
 *                   watches it while there are commented sentences, so the cards follow the editor as it fills in,
 *                   changes, wraps or switches to Source / Diff view (the comments then move to the list above).
 */

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { SbTooltip, TooltipTriggerStyled } from 'uiCore';
import { ChipRemoveStyled } from 'Components/JdOwnership/JdCurrentOwnerRow.styles';
import {
  COMMENTS_PANEL_TITLE, ORPHANS_TITLE, TOOLTIP_CLOSE_COMMENTS, TOOLTIP_RESOLVE_COMMENT, TOOLTIP_RESOLVE_THREAD, canonicalText,
  getAnchoredThreads, getOrphanThreads,
} from 'Utils/draftFeedback';
import {
  AnchoredCardsStyled, AnchoredCardsListStyled, AnchoredLayerStyled, CommentActionStyled, CommentCardStyled, CommentHighlightStyle,
  CommentMetaStyled, CommentTextStyled, CommentsGroupStyled, CommentsPanelStyled,
} from './DraftFeedback.styles';

export const HIGHLIGHT_NAME = 'jd-draft-comment';

const formatWhen = (iso) => {
  const when = iso ? new Date(iso) : null;
  return when && !Number.isNaN(when.getTime())
    ? when.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';
};

const BLOCKS = 'p, li, h1, h2, h3, h4, h5, h6';
const inCards = (el) => Boolean(el.closest('[data-comment-layer]'));
// the same for a mutated node (an element, or the text inside one)
const inCardsNode = (node) => {
  const el = node?.nodeType === 1 ? node : node?.parentElement;
  return Boolean(el) && inCards(el);
};
// The innermost editor blocks holding the text: a list item wrapping a nested list holds its items' text too, so the
// item itself, not its wrapper, is the match
const innermost = (matches) => matches.filter((el) => !matches.some((other) => other !== el && el.contains(other)));
const blocksWith = (root, key) => innermost([...root.querySelectorAll(BLOCKS)]
  .filter((el) => !inCards(el) && canonicalText(el.textContent).includes(key)));

// The editor's blocks (paragraphs, list items, headings) that hold a commented sentence, as ranges
export const commentedRanges = (root, anchors) => {
  if (!root || typeof document === 'undefined') return [];
  const keys = anchors.map(canonicalText).filter(Boolean);
  return [...new Set(keys.flatMap((key) => blocksWith(root, key)))].map((el) => {
    const range = document.createRange();
    range.selectNodeContents(el);
    return range;
  });
};

// The first (innermost) editor block holding the sentence that is laid out, outside the cards placed in the root.
// null when there is none, or when the rich text is hidden (MDXEditor's Source / Diff view sets display: none): the
// sentence's comments are then listed above the editor.
export const findCommentedBlock = (root, anchor) => {
  const key = canonicalText(anchor);
  if (!root || !key) return null;
  return blocksWith(root, key).find((el) => el.getClientRects().length > 0) || null;
};

// Space between a card and its sentence
const CARD_GAP = 6;
// The element the editor's text actually scrolls in (MDXEditor's content wrapper, inside the scroll area): the cards
// are placed in it, so they scroll with their line. Falls back to the given root.
const EDITOR_SCROLLER = '.mdxeditor-root-contenteditable';
export const cardsHost = (root) => root?.querySelector?.(EDITOR_SCROLLER) || root || null;
// The cards sit at the RIGHT of the sentence's block (the text tends to be on the left), this share of its width
// (never narrower than CARD_MIN_WIDTH, never wider than the block)
const CARD_WIDTH_SHARE = 0.6;
const CARD_MIN_WIDTH = 420;
export const cardPlace = (blockWidth) => {
  const width = Math.min(blockWidth, Math.max(CARD_MIN_WIDTH, blockWidth * CARD_WIDTH_SHARE));
  return { width, offset: blockWidth - width };
};
// Put a block's own top margin back (none: remove ours)
const restoreMargin = (block, own) => {
  if (own) block.style.setProperty('margin-top', own);
  else block.style.removeProperty('margin-top');
};
const samePlaces = (a, b) => {
  const keys = Object.keys(b);
  return keys.length === Object.keys(a).length && keys.every((k) => a[k]
    && ['top', 'left', 'width'].every((f) => Math.abs(a[k][f] - b[k][f]) < 0.5));
};

// The cards of each commented sentence, right above its block in the editor's scroll area (see the header). Each
// block gets a top margin the height of its cards; the cards are placed in that space. The panel re-renders this
// whenever the editor changes (its observers), so the places follow.
const AnchoredCards = ({ root, groups, renderThread, onClose }) => {
  const cardRefs = useRef(new Map());
  const spaced = useRef(new Map());   // block → its own margin-top, put back when it no longer has cards
  const [places, setPlaces] = useState({});

  // Every render: the editor's DOM changes outside React; setPlaces only when a place moved
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    const blocks = new Set(groups.map((g) => g.block));
    spaced.current.forEach((own, block) => {
      if (!blocks.has(block)) {
        restoreMargin(block, own);
        spaced.current.delete(block);
      }
    });
    groups.forEach(({ key, block }) => {
      const card = cardRefs.current.get(key);
      if (!card) return;
      if (!spaced.current.has(block)) spaced.current.set(block, block.style.marginTop);
      const margin = `${card.offsetHeight + CARD_GAP * 2}px`;
      // the editor's block, not React's: its style is set directly
      if (block.style.marginTop !== margin) block.style.setProperty('margin-top', margin);
    });
    const rootBox = root.getBoundingClientRect();
    const next = {};
    groups.forEach(({ key, block }) => {
      const card = cardRefs.current.get(key);
      if (!card) return;
      const box = block.getBoundingClientRect();
      const { width, offset } = cardPlace(box.width);
      next[key] = {
        top: box.top - rootBox.top + root.scrollTop - card.offsetHeight - CARD_GAP,
        left: box.left - rootBox.left + root.scrollLeft + offset,   // right-aligned with the sentence's block
        width,
      };
    });
    if (!samePlaces(places, next)) setPlaces(next);
  });

  // Gone (editing ended): give the blocks their own margins back, in the same commit
  useLayoutEffect(() => () => {
    spaced.current.forEach((own, block) => restoreMargin(block, own));
    spaced.current.clear();
  }, []);

  return createPortal(
    <AnchoredLayerStyled data-comment-layer="">
      {groups.map(({ key, threads }) => {
        const place = places[key];
        return (
          <AnchoredCardsStyled
            key={key}
            data-testid="anchored-comments"
            ref={(el) => { if (el) cardRefs.current.set(key, el); else cardRefs.current.delete(key); }}
            style={{
              top: place?.top ?? 0, left: place?.left ?? 0, width: place ? place.width : undefined,
              visibility: place ? 'visible' : 'hidden',
            }}
          >
            <AnchoredCardsListStyled>{threads.map(renderThread)}</AnchoredCardsListStyled>
            {/* Hide this sentence's comments while editing (nothing is resolved; they are back when the editor reopens) */}
            <SbTooltip content={TOOLTIP_CLOSE_COMMENTS}>
              <TooltipTriggerStyled>
                <ChipRemoveStyled type="button" aria-label="Close these comments" onClick={() => onClose(key)}>
                  ✕
                </ChipRemoveStyled>
              </TooltipTriggerStyled>
            </SbTooltip>
          </AnchoredCardsStyled>
        );
      })}
    </AnchoredLayerStyled>,
    root,
  );
};

AnchoredCards.propTypes = {
  root: PropTypes.object.isRequired,   // eslint-disable-line react/forbid-prop-types -- a DOM element
  groups: PropTypes.arrayOf(PropTypes.shape({ key: PropTypes.string, block: PropTypes.object,  // eslint-disable-line react/forbid-prop-types
    threads: PropTypes.arrayOf(PropTypes.object) })).isRequired,   // eslint-disable-line react/forbid-prop-types
  renderThread: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

const Card = ({ comment, isReply = false, resolveTip = null, busy = false, onResolve = undefined }) => (
  // One thin line: who and when, the comment (it wraps only when it is long), then Resolve at the right
  <CommentCardStyled $reply={isReply} $inline data-testid="editor-comment">
    <CommentMetaStyled $inline>
      {isReply && '↳ '}
      <b>{comment.author_name || comment.author_email}</b>
      {formatWhen(comment.created_at) && ` · ${formatWhen(comment.created_at)}`}
    </CommentMetaStyled>
    <CommentTextStyled>{comment.text}</CommentTextStyled>
    {resolveTip && (
      <SbTooltip content={resolveTip}>
        <TooltipTriggerStyled>
          <CommentActionStyled type="button" disabled={busy} onClick={() => onResolve(comment.feedback_id)}>
            ✓ Resolve
          </CommentActionStyled>
        </TooltipTriggerStyled>
      </SbTooltip>
    )}
  </CommentCardStyled>
);

Card.propTypes = {
  comment: PropTypes.shape({
    feedback_id: PropTypes.string, author_name: PropTypes.string, author_email: PropTypes.string,
    text: PropTypes.string, created_at: PropTypes.string,
  }).isRequired,
  isReply: PropTypes.bool,
  resolveTip: PropTypes.string,
  busy: PropTypes.bool,
  onResolve: PropTypes.func,
};

const DraftCommentsPanel = ({
  comments = [], markdown = '', canResolve = false, busy = false, onResolve = undefined, editorRootRef = null,
}) => {
  const orphans = getOrphanThreads(comments, markdown);
  const anchored = getAnchoredThreads(comments, markdown);
  // the editor's scroll area, once it is rendered (the ref is set after this panel's first render)
  const [root, setRoot] = useState(null);
  const [, setTick] = useState(0);
  // The sentences whose cards the holder closed (×): hidden until the editor is opened again, nothing is resolved
  const [closed, setClosed] = useState(() => new Set());
  const hasAnchored = anchored.length > 0;
  // Clicking a closed sentence's line in the editor opens its comments again
  useEffect(() => {
    if (!root || !closed.size) return undefined;
    const reopen = (event) => {
      const hit = [...closed].find((anchor) => findCommentedBlock(root, anchor)?.contains(event.target));
      if (hit) setClosed((prev) => { const next = new Set(prev); next.delete(hit); return next; });
    };
    root.addEventListener('click', reopen);
    return () => root.removeEventListener('click', reopen);
  }, [root, closed]);
  // Every render, after the whole commit (the editor's ref is attached after this panel's own effects): setRoot only
  // when the element changed
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const current = editorRootRef?.current || null;
    if (current !== root) setRoot(current);
  });
  // While there are commented sentences: look for their blocks again (and place their cards again) when the editor's
  // content or size changes — it fills in after mounting, it is typed in, it wraps — not for the cards' own changes
  useEffect(() => {
    if (!root || !hasAnchored) return undefined;
    const again = () => setTick((t) => t + 1);
    const mutations = typeof MutationObserver !== 'undefined' ? new MutationObserver((records) => {
      if (records.some((r) => !inCardsNode(r.target))) again();
    }) : null;
    mutations?.observe(root, { childList: true, subtree: true, characterData: true, attributes: true,
      attributeFilter: ['style', 'class'] });
    const resize = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(again) : null;
    resize?.observe(root);
    return () => {
      mutations?.disconnect();
      resize?.disconnect();
    };
  }, [root, hasAnchored]);
  // each sentence's block in the editor (sentences sharing a block share its cards); the rest stay listed here
  const groups = [];
  const unplaced = [];
  anchored.forEach(({ anchor, threads }) => {
    const block = findCommentedBlock(root, anchor);
    if (!block) {
      unplaced.push({ anchor, threads });
      return;
    }
    if (closed.has(anchor)) return;   // closed by the holder: no card (its block gets its own margin back)
    const group = groups.find((g) => g.block === block);
    if (group) group.threads.push(...threads);
    else groups.push({ key: anchor, block, threads: [...threads] });
  });

  useEffect(() => {
    const highlights = typeof CSS !== 'undefined' ? CSS.highlights : undefined;
    if (!highlights || typeof Highlight === 'undefined') return undefined;   // an older browser: no highlight
    const ranges = commentedRanges(editorRootRef?.current, anchored.map((a) => a.anchor));
    highlights.set(HIGHLIGHT_NAME, new Highlight(...ranges));   // eslint-disable-line no-undef -- a browser global
    return () => highlights.delete(HIGHLIGHT_NAME);
  });

  if (!orphans.length && !anchored.length) return null;
  const thread = ({ root: first, replies }) => (
    <React.Fragment key={first.feedback_id}>
      <Card comment={first} busy={busy} onResolve={onResolve}
        resolveTip={canResolve ? (replies.length ? TOOLTIP_RESOLVE_THREAD : TOOLTIP_RESOLVE_COMMENT) : null} />
      {replies.map((r) => <Card key={r.feedback_id} comment={r} isReply />)}
    </React.Fragment>
  );
  const closeCards = (key) => setClosed((prev) => new Set(prev).add(key));
  const cards = root && groups.length > 0
    && <AnchoredCards root={cardsHost(root)} groups={groups} renderThread={thread} onClose={closeCards} />;
  if (!orphans.length && !unplaced.length) return <><CommentHighlightStyle />{cards}</>;
  return (
    <CommentsPanelStyled data-testid="draft-comments-panel">
      <CommentHighlightStyle />
      {cards}
      {orphans.length > 0 && (
        <CommentsGroupStyled data-testid="orphan-comments">
          <b>{ORPHANS_TITLE}</b>
          {orphans.map((t) => (
            <React.Fragment key={t.root.feedback_id}>
              <q>{t.root.anchor_text.slice(0, 100)}</q>
              {thread(t)}
            </React.Fragment>
          ))}
        </CommentsGroupStyled>
      )}
      {unplaced.length > 0 && (
        <CommentsGroupStyled>
          <b>{COMMENTS_PANEL_TITLE}</b>
          {unplaced.map(({ anchor, threads }) => (
            <React.Fragment key={anchor}>
              <q>{anchor}</q>
              {threads.map(thread)}
            </React.Fragment>
          ))}
        </CommentsGroupStyled>
      )}
    </CommentsPanelStyled>
  );
};

DraftCommentsPanel.propTypes = {
  comments: PropTypes.arrayOf(PropTypes.object),
  markdown: PropTypes.string,
  canResolve: PropTypes.bool,
  busy: PropTypes.bool,
  onResolve: PropTypes.func,
  editorRootRef: PropTypes.shape({ current: PropTypes.any }),
};

export default DraftCommentsPanel;
